import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useModule } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Store,
  Truck,
  ArrowRight,
  Users,
  Package,
  MessageSquare,
  Building2,
  ShoppingCart,
  BarChart3,
  CreditCard
} from 'lucide-react';

const ModuleSelectionPage = () => {
  const navigate = useNavigate();
  const { setActiveModule, getUserModules } = useModule();
  const { user } = useAuth();

  const handleModuleSelect = (module) => {
    setActiveModule(module);
    navigate(`/${module}/dashboard`, { replace: true });
  };

  const modules = [
    {
      id: 'retail',
      name: 'Retail Management',
      description: 'Multi-branch retail operations, POS sales, inventory, and customer management',
      icon: Store,
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      borderColor: 'border-blue-500/30',
      features: [
        { icon: ShoppingCart, text: 'Point of Sale' },
        { icon: Package, text: 'Inventory Management' },
        { icon: Users, text: 'Customer Relations' },
        { icon: BarChart3, text: 'Sales Analytics' }
      ]
    },
    {
      id: 'wholesale',
      name: 'Wholesale Management',
      description: 'B2B order processing, associate management, and manufacturing partnerships',
      icon: Truck,
      color: 'from-emerald-500 to-emerald-600',
      hoverColor: 'hover:from-emerald-600 hover:to-emerald-700',
      borderColor: 'border-emerald-500/30',
      features: [
        { icon: MessageSquare, text: 'WhatsApp Orders' },
        { icon: Building2, text: 'Manufacturing Companies' },
        { icon: Users, text: 'Associate Management' },
        { icon: CreditCard, text: 'Credit & Invoicing' }
      ]
    },
    {
      id: 'accounting',
      name: 'Accounting & Finance',
      description: 'General ledger, financial statements, budgeting, and payroll management',
      icon: BarChart3,
      color: 'from-purple-500 to-purple-600',
      hoverColor: 'hover:from-purple-600 hover:to-purple-700',
      borderColor: 'border-purple-500/30',
      features: [
        { icon: BarChart3, text: 'Financial Reports' },
        { icon: Building2, text: 'Budgets & Expenses' },
        { icon: Users, text: 'Payroll Management' },
        { icon: CreditCard, text: 'Audit Logs' }
      ]
    }
  ];

  const allowedModules = getUserModules();
  const filteredModules = modules.filter((m) => allowedModules.includes(m.id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-black via-primary-charcoal to-primary-slate flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white mb-2">
            Welcome, {user?.full_name || 'Admin'}
          </h1>
          <p className="text-gray-400 text-lg">Select a module to get started</p>
        </div>

        {/* Module Cards */}
        <div
          className={`grid gap-6 ${
            filteredModules.length === 3
              ? 'md:grid-cols-3'
              : filteredModules.length === 2
                ? 'md:grid-cols-2'
                : 'max-w-md mx-auto'
          }`}
        >
          {filteredModules.map((module) => {
            const IconComponent = module.icon;

            return (
              <button
                key={module.id}
                onClick={() => handleModuleSelect(module.id)}
                className={`
                  group relative p-8 rounded-2xl border ${module.borderColor}
                  bg-white/5 backdrop-blur-sm
                  transition-all duration-300 ease-out
                  hover:bg-white/10 hover:scale-[1.02] hover:shadow-2xl
                  focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2 focus:ring-offset-primary-black
                  text-left
                `}
              >
                {/* Gradient Overlay on Hover */}
                <div
                  className={`
                  absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-10
                  bg-gradient-to-br ${module.color}
                  transition-opacity duration-300
                `}
                />

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div
                    className={`
                    w-16 h-16 rounded-xl mb-6
                    bg-gradient-to-br ${module.color}
                    flex items-center justify-center
                    shadow-lg
                  `}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                    {module.name}
                    <ArrowRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                  </h2>

                  {/* Description */}
                  <p className="text-gray-400 mb-6 leading-relaxed">{module.description}</p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-3">
                    {module.features.map((feature, index) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                          <FeatureIcon className="w-4 h-4 text-gray-500" />
                          <span>{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Arrow Indicator */}
                <div className="absolute top-8 right-8">
                  <div
                    className={`
                    w-10 h-10 rounded-full
                    bg-gradient-to-br ${module.color}
                    flex items-center justify-center
                    opacity-0 group-hover:opacity-100
                    transform translate-x-2 group-hover:translate-x-0
                    transition-all duration-300
                  `}
                  >
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          You can switch between modules anytime using the module switcher in the navigation bar
        </p>
      </div>
    </div>
  );
};

export default ModuleSelectionPage;
